<template>
  <section
    :id="blockId"
    ref="cardScroller"
    :class="
      bem({
        '--bg-colour-': props.block.backgroundColour,
      })
    "
  >
    <div
      v-if="props.block.title"
      :class="bem('inner')"
    >
      <div :id="titleWrapperId" :class="bem('title-wrapper')">
        <h6 v-if="props.block.pretitle" :class="bem('pretitle')">
          {{ props.block.pretitle }}
        </h6>
        <h2 :class="bem('title')">
          <span :id="titleId">
            <CardScrollerRevealTitle
              :bg-colour="props.block.backgroundColour"
              :heading="props.block.title.titleTextReveal"
            />
          </span>
        </h2>
      </div>
    </div>

    <div v-if="props.block.cards.length" :class="bem('scroll-wrapper')">
        <ul ref="cardScrollerList" :class="bem('scroll-list')">
          <li v-for="(card, index) in block.cards" :key="card.id" :class="bem('scroll-list-item')">
            <div :class="[bem('card-contents')]" :data-card-scroller-card="index">
              <UniversalCard
                v-if="card.type === 'card'"
                :item="card"
                :aspect-ratio="'1/1'"
              />
              <CardScrollerCard
                v-else-if="card.type === 'card_scroller_card'"
                :item="card"
              />
            </div>
          </li>
        </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import tailwindConfig from '@/tailwind.config.js'
import resolveConfig from 'tailwindcss/resolveConfig'

interface CardScrollerProps {
  block: {
    backgroundColour?: 'black' | 'white'
    id?: string
    title?: {
      text: string
    }
    pretitle?: string
    cards: Array<any>
  }
}

const props = withDefaults(defineProps<CardScrollerProps>(), {
  block: () => ({
    backgroundColour: 'black',
    cards: []
  }),
})

const bem = useBEM('CardScroller')
const { gsap } = useGsap()
const resolvedConfig = resolveConfig(tailwindConfig)

const cardScroller = ref()
const cardScrollerList = ref()
const titleWrapperId = ref('CardScroller__title-wrapper-' + props.block.id)
const titleId = ref('CardScroller__title-' + props.block.id)
const blockId = ref('CardScroller-' + props.block.id)

onMounted(async () => {
  await nextTick();

  const animationScreenSize = gsap.matchMedia()

  // // Vertical Scrolling
  animationScreenSize.add(`(max-width: ${resolvedConfig.theme.screens.smMax.max})`, () => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.CardScroller__title-wrapper',
        start: 'center center',
        end: '140% center',
        scrub: 1.2,
        invalidateOnRefresh: true,
        pin: true
      },
    })

    /**
     *
     * Manually resets the scroll list to the position it should be in on mobile
     * should the users resize from desktop to mobile to ensure no random values
     * are computed during the resizes and GSAP calculations.
     *
     */
    gsap.set(`#${blockId.value} .CardScrollerRevealTitle__scroll-list`, {
      x: () => 0
    })

    const titleFillAreas = gsap.utils.toArray(`#${blockId.value} .CardScrollerRevealTitle__title-fill-area`)

    titleFillAreas.forEach(titleFillArea => {
      timeline.set(titleFillArea, {
        backgroundSize: '0% 100%',
      })

      timeline.to(titleFillArea, {
        backgroundSize: '100% 100%',
        ease: 'none',
      })
    })

    timeline.to('#' + titleWrapperId.value, {
      opacity: '0',
      scale: 0.6,
      y: 0,
      ease: 'none',
    })

    if (props.block.cards.length) {

      const cards = gsap.utils.toArray(`#${blockId.value} .CardScroller__scroll-list-item`)
      
      cards.forEach((card, index) => {
        timeline.to(`#${blockId.value} [data-card-scroller-card="${index}"]`, {
          opacity: index + 1 === cards.length ? 0.8 : 0,
          ease: 'none',
          scale: () => 1 - (cards.length - index) * 0.025,
          scrollTrigger: {
            trigger: card,
            start: () => `75% center`,
            end: () => {
              return `110% 30%`
            },
            scrub: 1,
            invalidateOnRefresh: true
          }
        })
      })
    }
  })

  // // Horizontal Scrolling
  animationScreenSize.add(`(min-width: ${resolvedConfig.theme.screens.sm})`, () => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.CardScroller',
        start: 'top top',
        end: '75% center',
        scrub: 1.5,
        invalidateOnRefresh: true
      },
    })

    gsap.set(`#${blockId.value} .CardScroller__scroll-list`, {
      x: () => '100%'
    })

    const titleFillAreas = gsap.utils.toArray(`#${titleId.value} .CardScrollerRevealTitle__title-fill-area`)

    titleFillAreas.forEach(titleFillArea => {
      timeline.set(titleFillArea, {
        backgroundSize: '0% 100%',
      })

      timeline.to(titleFillArea, {
        backgroundSize: '100% 100%',
        ease: 'none',
      })
    })

    timeline.to('#' + titleWrapperId.value, {
      opacity: '0',
      scale: 0.6,
      ease: 'none',
    })

    if (props.block.cards.length) {
      timeline.to(`#${blockId.value} .CardScroller__scroll-list`, {
        x: () => {
          /**
           * Calculates the amount overflowing to always make sure all items are scrollable into view.
           *
           * Gets width of the container and scrolls it to the left by that amount minus the width
           * of the window to stop all cards scrolling completely out of view and ends the
           * scroll transition with cards still in view.
           *
           **/
          if(!cardScrollerList.value) return '-50%'

          return '-' + (cardScrollerList.value.scrollWidth - window.innerWidth / 1.5)  + 'px'
        },
        ease: 'none',
        scrollTrigger: {
          trigger: `#${blockId.value} .CardScroller__scroll-wrapper`,
          scrub: 1,
          pin: true,
          invalidateOnRefresh: true,
          start: () => document.querySelector(`#${blockId.value} .CardScroller__scroll-list`).offsetHeight / 2 + 'px' + ' center',
          end: () => window.innerHeight + 'px' + ' center',
        }
      })
    }
  })
})
</script>

<style lang="postcss">
.CardScroller {
  @apply flex flex-col bg-black w-full text-white;
}

.CardScroller--bg-colour-white {
  @apply bg-white;

  .CardScroller__pretitle {
    @apply text-black;
  }
}

.CardScroller__inner {
  @apply sticky top-0 flex items-center justify-center min-h-[100dvh];
}

.CardScroller__pretitle {
  @apply text-center mb-6 font-fragment-mono text-xxs;
}

.CardScroller__title {
  @apply text-center heading-1 text-2xl sm:text-6xl global-wrapper max-w-5xl;
}

.CardScroller__scroll-wrapper {
  @apply min-w-full min-h-[150dvh];

  @screen sm {
    /**
      Offset the height of the title reveal area to avoid the cards scrolling
      in before the text reveal has finished 'revealing'.
    */
    @apply mt-[100dvh];
  }

  @screen smMax {
    @apply pb-[25dvh];
  }
}

.CardScroller__scroll-list {
  @apply flex items-center justify-start flex-col gap-4 flex-shrink-0
    translate-x-0;

  @screen sm {
    @apply flex-row items-start
      translate-x-full;
  }
}

.CardScroller__scroll-list-item {
  @apply flex items-center justify-center flex-shrink-0
    w-1/4 min-w-80;

  @screen smMax {
    @apply w-full sticky top-1/4;
  }
}

.CardScroller__card-contents {
  @apply w-full;

  @screen smMax {
    @apply px-4;
  }
}
</style>
